import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, Users, MapPin, Calendar, BookOpen, Scroll, Library, Pencil } from 'lucide-react';
import { useCultureByPassageData } from '@/data/culture/culture-by-passage-data-context';
import { CultureByBookDataContext } from '@/data/culture/culture-by-book-data-context';
import { usePeopleByPassageData } from '@/data/people/people-by-passage-data-context';
import { PeopleDataContext } from '@/data/people/people-data-context';
import { useHistoricalEventsByPassageData } from '@/data/historical-events/historical-events-by-passage-data-context';
import { HistoricalEventsByBookDataContext } from '@/data/historical-events/historical-events-by-book-data-context';
import { useLiteraryFeaturesByPassageData } from '@/data/literary-features/literary-features-by-passage-data-context';
import { LiteraryFeaturesDataContext } from '@/data/literary-features/literary-feature-data-context';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { useArchaeologyByPassageData } from '@/data/archaeology/archaeology-by-passage-data-context';
import { ArchaeologyByBookDataContext } from '@/data/archaeology/archaeology-by-book-data-context';
import { CulturalPointCard } from './cultural-point-card';
import { CulturalTermCard } from './cultural-term-card';
import { CulturalThemeCard } from './cultural-theme-card';
import { AneParallelCard } from './ane-parallel-card';
import { SensoryContextCard } from './sensory-context-card';
import { PersonCard } from './person-card';
import { SocialWorldCard } from './social-world-card';
import { ArchaeologicalFindingCard } from './archaeological-finding-card';
import { CompositionDetailsCard } from './composition-details-card';
import { NarrativeSettingCard } from './narrative-setting-card';
import { ThemesList } from './themes-list';
import { AllusionCard } from './allusion-card';
import { SynopticParallelCard } from './synoptic-parallel-card';
import PassageGenreGuide from './passage-genre-guide';
import { MicroStructureCard } from './micro-structure-card';
import { BookOutlineSection } from './book-outline-section';
import { WordplayCard } from './wordplay-card';
import { ImageryCard } from './imagery-card';
import { LiteraryDeviceCard } from './literary-device-card';
import { NarrativeTechniqueCard } from './narrative-technique-card';
import { PoeticFeatureCard } from './poetic-feature-card';
import { OralTraditionCard } from './oral-tradition-card';
import { MacroStructureCard } from './macro-structure-card';
import { useState, useMemo, use, useEffect } from 'react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useUrl } from 'crossroad';
import { logWarning } from '@/lib/error-logging';
import { IconTrowel } from '@tabler/icons-react';

interface CapabilityContextModalProps {
  passageId: string;
  capabilityRef: string;
  capabilityItems: CapabilityItemReference[];
  isOpen: boolean;
  onClose: () => void;
  bookName?: string;
}

/**
 * Parse capabilityRef into capability and optional feature
 * e.g., "cultural-context:key-points" -> { capability: "cultural-context", feature: "key-points" }
 */
function parseCapabilityRef(ref: string): { capability: string; feature?: string } {
  const parts = ref.split(':');
  return {
    capability: parts[0],
    feature: parts[1]
  };
}

/**
 * Infer the feature ID from capability items when it's missing from capabilityRef
 * This handles cases where AI generated incomplete references like "cultural-context" instead of "cultural-context:senses"
 */
function inferFeatureFromItems(capability: string, items: CapabilityItemReference[]): string | undefined {
  if (capability !== 'cultural-context' || items.length === 0) {
    return undefined;
  }

  const itemTitles = items.map(item => item.itemTitle.toLowerCase());

  // Check for sensory context items
  const sensoryKeywords = ['setting', 'sights', 'sounds', 'smells', 'atmosphere'];
  if (itemTitles.some(title => sensoryKeywords.includes(title))) {
    return 'senses';
  }

  // Check for cultural terms (often have transliterations in parentheses or language indicators)
  if (itemTitles.some(title => title.includes('(') && title.includes(')'))) {
    return 'key-cultural-terms';
  }

  // Default to key-points for other cultural context items
  return 'key-points';
}

/**
 * Get icon for capability type
 */
function getCapabilityIcon(capability: string, feature?: string) {
  if (capability === 'cultural-context') {
    if (feature === 'people') return <Users className="h-5 w-5" />;
    if (feature === 'key-points') return <Sparkles className="h-5 w-5" />;
    return <Scroll className="h-5 w-5" />;
  }
  if (capability === 'geographical-context') return <MapPin className="h-5 w-5" />;
  if (capability === 'historical-context') return <Calendar className="h-5 w-5" />;
  if (capability === 'literary-context') return <BookOpen className="h-5 w-5" />;
  if (capability === 'book-context') return <Library className="h-5 w-5" />;
  if (capability === 'archaeological-context') return <IconTrowel className="h-5 w-5" />;
  if (capability === 'manuscript-study') return <Pencil className="h-5 w-5" />;
  return <Sparkles className="h-5 w-5" />;
}

/**
 * Get title for the modal based on capability type
 */
function getCapabilityTitle(capability: string, feature?: string): string {
  if (capability === 'cultural-context') {
    if (feature === 'key-points') return 'Cultural Context';
    if (feature === 'people') return 'People in the Passage';
    if (feature === 'senses') return 'Sensory Context';
    if (feature === 'key-cultural-terms') return 'Key Cultural Terms';
    if (feature === 'cultural-themes') return 'Cultural Themes';
    if (feature === 'ancient-parallels') return 'Ancient Near Eastern Parallels';
    return 'Cultural Context';
  }
  if (capability === 'historical-context') {
    if (feature === 'timeline') return 'Timeline Events';
    if (feature === 'narrative-setting') return 'Narrative Setting';
    return 'Historical Context';
  }
  if (capability === 'literary-context') {
    if (feature === 'literary-devices') return 'Literary Devices';
    if (feature === 'micro-structures') return 'Literary Structures';
    if (feature === 'genre') return 'Genre Analysis';
    return 'Literary Context';
  }
  if (capability === 'book-context') return 'Book Context';
  if (capability === 'archaeological-context') return 'Archaeology';
  if (capability === 'manuscript-study') {
    if (feature === 'connections') return 'Textual Connections';
    if (feature === 'word-categories') return 'Word Patterns';
    if (feature === 'notes') return 'Insights & Annotations';
    return 'Manuscript Study';
  }
  return 'Context';
}

/**
 * Render content based on capability type, filtered to matching items
 */
function CapabilityContent({
  capability,
  feature,
  itemTitles,
  cultureData,
  cultureByBookData,
  peopleData,
  peopleContext,
  historicalData,
  historicalByBookData,
  literaryData,
  literaryByBookData,
  outline,
  passage,
  archaeologyData,
  archaeologyByBookData,
  bookName,
  expandedItems,
  toggleExpanded,
  onClose
}: {
  capability: string;
  feature?: string;
  itemTitles: Set<string>;
  cultureData?: CultureByPassage;
  cultureByBookData?: CultureByBook;
  peopleData?: PeopleByPassage;
  peopleContext?: {
    getPersonByName: (personName: string) => Person | undefined;
  };
  historicalData?: HistoricalEventsByPassage;
  historicalByBookData?: HistoricalEventsByBook;
  literaryData?: LiteraryFeaturesByPassage;
  literaryByBookData?: LiteraryFeaturesByBook;
  outline?: Outline;
  passage?: Passage;
  archaeologyData?: ArchaeologyByPassage;
  archaeologyByBookData?: ArchaeologyByBook;
  bookName?: string;
  expandedItems: Set<string>;
  toggleExpanded: (id: string) => void;
  onClose: () => void;
}) {
  // Log warning if expected data is missing
  useEffect(() => {
    const capabilityRef = feature ? `${capability}:${feature}` : capability;
    const expectedDataMissing: string[] = [];

    // Check for missing data based on capability type
    if (capability === 'cultural-context') {
      if (!cultureData && !cultureByBookData) {
        expectedDataMissing.push('cultureData (passage-level)', 'cultureByBookData (book-level)');
      }
      if (feature === 'people' && !peopleData && !peopleContext) {
        expectedDataMissing.push('peopleData', 'peopleContext');
      }
    }

    if (capability === 'historical-context') {
      if (!historicalData && !historicalByBookData) {
        expectedDataMissing.push('historicalData (passage-level)', 'historicalByBookData (book-level)');
      }
    }

    if (capability === 'literary-context') {
      if (!literaryData && !literaryByBookData) {
        expectedDataMissing.push('literaryData (passage-level)', 'literaryByBookData (book-level)');
      }
      if (feature === 'genre' && !passage) {
        expectedDataMissing.push('passage (needed for genre)');
      }
    }

    if (capability === 'archaeological-context') {
      if (!archaeologyData && !archaeologyByBookData) {
        expectedDataMissing.push('archaeologyData (passage-level)', 'archaeologyByBookData (book-level)');
      }
    }

    if (capability === 'book-context' && feature === 'outline') {
      if (!outline) {
        expectedDataMissing.push('outline');
      }
    }

    // Log warning if any expected data is missing
    if (expectedDataMissing.length > 0) {
      logWarning('Capability modal opened with missing context data', {
        component: 'CapabilityContextModal',
        additionalInfo: {
          capabilityRef,
          capability,
          feature,
          passageId: passage?.id,
          passageRef: passage?.ref,
          book: passage?.book || bookName,
          itemTitles: Array.from(itemTitles),
          missingData: expectedDataMissing,
          availableData: {
            hasCultureData: !!cultureData,
            hasCultureByBookData: !!cultureByBookData,
            hasPeopleData: !!peopleData,
            hasPeopleContext: !!peopleContext,
            hasHistoricalData: !!historicalData,
            hasHistoricalByBookData: !!historicalByBookData,
            hasLiteraryData: !!literaryData,
            hasLiteraryByBookData: !!literaryByBookData,
            hasArchaeologyData: !!archaeologyData,
            hasArchaeologyByBookData: !!archaeologyByBookData,
            hasOutline: !!outline,
            hasPassage: !!passage,
          }
        }
      });
    }
  }, [capability, feature, cultureData, cultureByBookData, peopleData, peopleContext,
    historicalData, historicalByBookData, literaryData, literaryByBookData,
    archaeologyData, archaeologyByBookData, outline, passage, bookName, itemTitles]);

  const isFiltering = itemTitles.size > 0;

  // Cultural Context - Key Points (search both passage and book level)
  if (capability === 'cultural-context' && feature === 'key-points') {
    const passagePoints = cultureData?.keyPoints || [];
    const bookThemes = cultureByBookData?.culturalThemes || [];

    const matchingPassagePoints = passagePoints.filter(
      point => !isFiltering || itemTitles.has(point.title.toLowerCase())
    );

    // Book themes have same structure as passage points, can use same card
    const matchingBookThemes = bookThemes.filter(
      theme => !isFiltering || itemTitles.has(theme.title.toLowerCase())
    );

    if (matchingPassagePoints.length === 0 && matchingBookThemes.length === 0) {
      return <NoMatchingItems />;
    }

    return (
      <div className="space-y-4">
        {/* Passage-specific points first */}
        {matchingPassagePoints.length > 0 && (
          <>
            {matchingPassagePoints.length > 0 && matchingBookThemes.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Passage-Specific Points
              </div>
            )}
            {matchingPassagePoints.map((point, idx) => {
              const itemId = `passage-point-${idx}`;
              return (
                <CulturalPointCard
                  key={itemId}
                  point={point}
                  bookName={bookName}
                  isExpanded
                  onToggleExpansion={() => toggleExpanded(itemId)}
                />
              );
            })}
          </>
        )}

        {/* Book-level themes */}
        {matchingBookThemes.length > 0 && (
          <>
            {matchingPassagePoints.length > 0 && matchingBookThemes.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
                Book-Level Themes
              </div>
            )}
            {matchingBookThemes.map((theme, idx) => {
              const itemId = `book-theme-${idx}`;
              return (
                <CulturalThemeCard
                  key={itemId}
                  theme={theme}
                  isExpanded
                  onToggleExpansion={() => toggleExpanded(itemId)}
                />
              );
            })}
          </>
        )}
      </div>
    );
  }

  // Cultural Context - People
  if (capability === 'cultural-context' && feature === 'people' && peopleData?.people) {
    const matchingPeople = peopleData.people.filter(
      person => !isFiltering || itemTitles.has(person.personName.toLowerCase())
    );

    if (matchingPeople.length === 0) {
      return <NoMatchingItems />;
    }

    return (
      <div className="space-y-4">
        {matchingPeople.map((personInPassage, idx) => {
          const itemId = `person-${idx}`;
          const person = peopleContext?.getPersonByName(personInPassage.personName);

          return (
            <PersonCard
              key={idx}
              personInPassage={personInPassage}
              person={person}
              bookName={bookName}
              isExpanded
              onToggleExpansion={() => toggleExpanded(itemId)}
            />
          );
        })}
      </div>
    );
  }

  // Cultural Context - Senses
  if (capability === 'cultural-context' && feature === 'senses') {
    if (!cultureData?.sensoryContext) {
      return <NoMatchingItems />;
    }

    // Filter sensory context if specific items are requested
    // This allows the guided study to reveal senses one by one
    const filteredContext = { ...cultureData.sensoryContext };

    // Only filter if they didn't ask for the whole thing and provided specific list
    const requestedWhole = itemTitles.has('senses') || itemTitles.has('sensory context') || itemTitles.has('atmosphere');

    if (isFiltering && !requestedWhole) { // Only filter if isFiltering is true
      if (!itemTitles.has('setting')) delete filteredContext.setting;
      if (!itemTitles.has('sights')) delete filteredContext.sights;
      if (!itemTitles.has('sounds')) delete filteredContext.sounds;
      if (!itemTitles.has('smells')) delete filteredContext.smells;
      if (!itemTitles.has('atmosphere')) delete filteredContext.atmosphere;
    }

    return <SensoryContextCard sensoryContext={filteredContext} defaultExpanded />;
  }

  // Cultural Context - Key Cultural Terms (search both passage and book level)
  if (capability === 'cultural-context' && feature === 'key-cultural-terms') {
    const passageTerms = cultureData?.keyTerms || [];
    const bookTerms = cultureByBookData?.commonTerms || [];

    const matchingPassageTerms = passageTerms.filter(
      term => !isFiltering || itemTitles.has(term.term.toLowerCase()) || itemTitles.has(term.transliteration?.toLowerCase() || '')
    );

    const matchingBookTerms = bookTerms.filter(
      term => !isFiltering || itemTitles.has(term.term.toLowerCase()) || itemTitles.has(term.transliteration?.toLowerCase() || '')
    );

    if (matchingPassageTerms.length === 0 && matchingBookTerms.length === 0) {
      return <NoMatchingItems />;
    }

    return (
      <div className="space-y-4">
        {/* Passage-specific terms first */}
        {matchingPassageTerms.length > 0 && (
          <>
            {matchingPassageTerms.length > 0 && matchingBookTerms.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Passage-Specific Terms
              </div>
            )}
            {matchingPassageTerms.map((term, idx) => {
              const itemId = `passage-term-${idx}`;
              return (
                <CulturalTermCard
                  key={itemId}
                  term={term}
                  bookName={bookName}
                  isExpanded
                  onToggleExpansion={() => toggleExpanded(itemId)}
                />
              );
            })}
          </>
        )}

        {/* Book-level common terms */}
        {matchingBookTerms.length > 0 && (
          <>
            {matchingPassageTerms.length > 0 && matchingBookTerms.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
                Book-Level Common Terms
              </div>
            )}
            {matchingBookTerms.map((term, idx) => {
              const itemId = `book-term-${idx}`;
              return (
                <CulturalTermCard
                  key={itemId}
                  term={term}
                  bookName={bookName}
                  isExpanded
                  onToggleExpansion={() => toggleExpanded(itemId)}
                />
              );
            })}
          </>
        )}
      </div>
    );
  }

  // Cultural Context - Social World
  if (capability === 'cultural-context' && feature === 'social-world') {
    if (!cultureByBookData?.socialWorld) {
      return <NoMatchingItems />;
    }

    return <SocialWorldCard socialWorld={cultureByBookData.socialWorld} />;
  }

  // Cultural Context - Cultural Themes
  if (capability === 'cultural-context' && feature === 'cultural-themes' && cultureByBookData?.culturalThemes) {
    const matchingThemes = cultureByBookData.culturalThemes.filter(
      theme => !isFiltering || itemTitles.has(theme.title.toLowerCase())
    );

    if (matchingThemes.length === 0) {
      return <NoMatchingItems />;
    }

    // Use existing CulturalThemeCard component
    return (
      <div className="space-y-4">
        {matchingThemes.map((theme, idx) => {
          const itemId = `theme-${idx}`;
          return (
            <CulturalThemeCard
              key={idx}
              theme={theme}
              isExpanded
              onToggleExpansion={() => toggleExpanded(itemId)}
            />
          );
        })}
      </div>
    );
  }

  // Cultural Context - Ancient Parallels
  if (capability === 'cultural-context' && feature === 'ancient-parallels' && cultureByBookData?.ancientNearEasternParallels) {
    const matchingParallels = cultureByBookData.ancientNearEasternParallels.filter(
      parallel => !isFiltering || itemTitles.has(parallel.parallel.toLowerCase())
    );

    if (matchingParallels.length === 0) {
      return <NoMatchingItems />;
    }

    // Use existing AneParallelCard component
    return (
      <div className="space-y-4">
        {matchingParallels.map((parallel, idx) => {
          const itemId = `parallel-${idx}`;
          return (
            <AneParallelCard
              key={idx}
              parallel={parallel}
              isExpanded
              onToggleExpansion={() => toggleExpanded(itemId)}
            />
          );
        })}
      </div>
    );
  }

  // Historical Context - Timeline Events (search both passage and book level)
  if (capability === 'historical-context') {
    const passageEvents = historicalData?.events || [];
    const bookEvents = historicalByBookData?.events || [];

    const matchingPassageEvents = passageEvents.filter(
      event => !isFiltering || itemTitles.has(event.title.toLowerCase())
    );

    const matchingBookEvents = bookEvents.filter(
      event => !isFiltering || itemTitles.has(event.title.toLowerCase())
    );

    if (matchingPassageEvents.length === 0 && matchingBookEvents.length === 0) {
      return <NoMatchingItems />;
    }

    return (
      <div className="space-y-4">
        {/* Passage-specific events first */}
        {matchingPassageEvents.length > 0 && (
          <>
            {matchingPassageEvents.length > 0 && matchingBookEvents.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Passage-Specific Events
              </div>
            )}
            {matchingPassageEvents.map((event, idx) => (
              <div key={`passage-event-${idx}`} className="border rounded-lg p-4 space-y-2">
                <h4 className="text-lg font-semibold">{event.title}</h4>
                {event.summary && <p className="text-sm">{event.summary}</p>}
                {event.description && (
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                )}
                {event.relevance && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1">Passage Relevance</p>
                    <p className="text-sm">{event.relevance}</p>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Book-level events */}
        {matchingBookEvents.length > 0 && (
          <>
            {matchingPassageEvents.length > 0 && matchingBookEvents.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
                Book-Level Events
              </div>
            )}
            {matchingBookEvents.map((event, idx) => (
              <div key={`book-event-${idx}`} className="border rounded-lg p-4 space-y-2">
                <h4 className="text-lg font-semibold">{event.title}</h4>
                {event.summary && <p className="text-sm">{event.summary}</p>}
                {event.description && (
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                )}
                {event.relevance && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1">Book Relevance</p>
                    <p className="text-sm">{event.relevance}</p>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // Historical Context - Composition Details
  if (capability === 'historical-context' && feature === 'composition-details' && historicalByBookData?.composition) {
    return <CompositionDetailsCard composition={historicalByBookData.composition} />;
  }

  // Historical Context - Narrative Setting
  if (capability === 'historical-context' && feature === 'narrative-setting' && historicalByBookData?.narrativeSetting) {
    return <NarrativeSettingCard narrativeSetting={historicalByBookData.narrativeSetting} />;
  }

  // Book Context - Outline
  if (capability === 'book-context' && feature === 'outline' && outline) {
    const passageContext = use(PassageDataContext);
    const allPassages = passageContext.data?.items || [];

    // Create a map of passage ID to passage for quick lookup
    const passageMap = new Map<string, Passage>();
    allPassages.forEach(p => passageMap.set(p.id, p));

    return (
      <BookOutlineSection
        outline={outline}
        passageId={passage?.id}
        passageMap={passageMap}
        visibleNodeIds={undefined}
      />
    );
  }

  // Book Context - Macro Structure
  if (capability === 'book-context' && feature === 'macro-structure' && literaryByBookData?.macroStructure) {
    return (
      <div className="space-y-4">
        {literaryByBookData.macroStructure.map((structure, idx) => (
          <MacroStructureCard
            key={idx}
            structure={structure}
          />
        ))}
      </div>
    );
  }

  // Book Context - Repeated Patterns
  if (capability === 'book-context' && feature === 'repeated-patterns' && literaryByBookData?.repeatedPatterns) {
    return (
      <div className="space-y-3">
        {literaryByBookData.repeatedPatterns.map((pattern, idx) => (
          <div key={idx} className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">{pattern.label}</h4>
              <Badge variant="outline">{pattern.kind}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">Occurs {pattern.count} times</p>
            {pattern.refs && pattern.refs.length > 0 && (
              <p className="text-sm">References: {pattern.refs.join(', ')}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Book Context - Theme Trajectory
  if (capability === 'book-context' && feature === 'theme-trajectory' && literaryByBookData?.themeTrajectory) {
    return (
      <div className="space-y-4">
        {literaryByBookData.themeTrajectory.map((trajectory, idx) => (
          <div key={idx} className="border rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-lg">{trajectory.theme}</h4>
            <div className="space-y-2">
              {trajectory.trajectory.map((step, stepIdx) => (
                <div key={stepIdx} className="pl-4 border-l-2 border-muted">
                  <p className="text-sm font-medium">Step {stepIdx + 1}</p>
                  <p className="text-sm">{step.step}</p>
                  {step.refs && step.refs.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{step.refs.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Scriptural Context - Themes
  if (capability === 'scriptural-context' && feature === 'themes' && passage?.themes) {
    return <ThemesList themes={passage.themes} />;
  }

  // Scriptural Context - Allusions
  if (capability === 'scriptural-context' && feature === 'allusions' && literaryData?.allusions) {
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();

    const matchingAllusions = literaryData.allusions.filter(
      allusion => !isFiltering || itemTitles.has(allusion.text.toLowerCase()) || itemTitles.has(allusion.sourceRef?.toLowerCase() || '')
    );

    if (matchingAllusions.length === 0) {
      return <NoMatchingItems />;
    }

    return (
      <div className="space-y-4">
        {matchingAllusions.map((allusion, idx) => (
          <AllusionCard
            key={idx}
            allusion={allusion}
            isDarkMode={isDarkMode}
            setUrl={setUrl}
          />
        ))}
      </div>
    );
  }

  // Scriptural Context - Synoptic Parallels
  if (capability === 'scriptural-context' && feature === 'synoptic-parallels' && literaryData?.synopticParallels) {
    const [, setUrl] = useUrl();

    return (
      <div className="space-y-4">
        {literaryData.synopticParallels.map((parallel, idx) => (
          <SynopticParallelCard
            key={idx}
            parallel={parallel}
            bookName={bookName || ''}
            setUrl={setUrl}
          />
        ))}
      </div>
    );
  }

  // Literary Context - Genre
  if (capability === 'literary-context' && feature === 'genre' && passage) {
    const [genreExpanded, setGenreExpanded] = useState(new Set(['genre']));
    const toggleGenreSection = (section: string) => {
      setGenreExpanded(prev => {
        const next = new Set(prev);
        if (next.has(section)) {
          next.delete(section);
        } else {
          next.add(section);
        }
        return next;
      });
    };

    return (
      <PassageGenreGuide
        passage={passage}
      />
    );
  }

  // Literary Context - Literary Devices
  if (capability === 'literary-context' && feature === 'literary-devices' && literaryData?.literaryDevices) {
    const matchingDevices = literaryData.literaryDevices.filter(
      device => !isFiltering || itemTitles.has(device.device.toLowerCase()) || itemTitles.has(device.text?.toLowerCase() || '')
    );

    if (matchingDevices.length === 0) {
      return <NoMatchingItems />;
    }

    return (
      <div className="space-y-4">
        {matchingDevices.map((device, idx) => (
          <LiteraryDeviceCard
            key={idx}
            device={device}
          />
        ))}
      </div>
    );
  }

  // Literary Context - Micro Structures
  if (capability === 'literary-context' && feature === 'micro-structures' && literaryData?.microStructures) {
    return (
      <div className="space-y-4">
        {literaryData.microStructures.map((structure, idx) => {
          const itemId = `micro-${idx}`;
          return (
            <MicroStructureCard
              key={idx}
              structure={structure}
              isExpanded
              onToggleExpansion={() => toggleExpanded(itemId)}
            />
          );
        })}
      </div>
    );
  }

  // Literary Context - Wordplay
  if (capability === 'literary-context' && feature === 'wordplay' && literaryData?.wordplay) {
    return (
      <div className="space-y-4">
        {literaryData.wordplay.map((wordplay, idx) => {
          const itemId = `wordplay-${idx}`;
          return (
            <WordplayCard
              key={idx}
              wordplay={wordplay}
            />
          );
        })}
      </div>
    );
  }

  // Literary Context - Imagery
  if (capability === 'literary-context' && feature === 'imagery' && literaryData?.imagery) {
    return (
      <div className="space-y-4">
        {literaryData.imagery.map((imagery, idx) => {
          const itemId = `imagery-${idx}`;
          return (
            <ImageryCard
              key={idx}
              imagery={imagery}
            />
          );
        })}
      </div>
    );
  }

  // Literary Context - Narrative Techniques
  if (capability === 'literary-context' && feature === 'narrative-techniques' && literaryData?.narrativeTechniques) {
    return (
      <div className="space-y-4">
        {literaryData.narrativeTechniques.map((technique, idx) => {
          const itemId = `narrative-${idx}`;
          return (
            <NarrativeTechniqueCard
              key={idx}
              technique={technique}
            />
          );
        })}
      </div>
    );
  }

  // Literary Context - Poetic Features
  if (capability === 'literary-context' && feature === 'poetic-features' && literaryData?.poeticFeatures) {
    return (
      <div className="space-y-4">
        {literaryData.poeticFeatures.map((feature, idx) => {
          const itemId = `poetic-${idx}`;
          return (
            <PoeticFeatureCard
              key={idx}
              feature={feature}
            />
          );
        })}
      </div>
    );
  }

  // Literary Context - Oral Tradition
  if (capability === 'literary-context' && feature === 'oral-tradition' && literaryByBookData?.oralTradition) {
    return <OralTraditionCard oralTradition={literaryByBookData.oralTradition} isExpanded onToggleExpansion={() => { }} />;
  }

  // Archaeology - Filter by capabilityItems if provided
  if (capability === 'archaeological-context') {
    const passageFindings = archaeologyData?.findings || [];
    const bookFindings = archaeologyByBookData?.findings || [];

    // Filter findings by itemTitles if capabilityItems were specified
    const filteredPassageFindings = isFiltering
      ? passageFindings.filter(finding => itemTitles.has(finding.title.toLowerCase()))
      : passageFindings;

    const filteredBookFindings = isFiltering
      ? bookFindings.filter(finding => itemTitles.has(finding.title.toLowerCase()))
      : bookFindings;

    const allFindings = [...filteredPassageFindings, ...filteredBookFindings];

    if (allFindings.length === 0) {
      return itemTitles.size > 0 ? <NoMatchingItems /> : (
        <div className="text-center py-8 text-muted-foreground">
          <p>No archaeological findings available for this passage or book.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Passage-level findings first */}
        {filteredPassageFindings.length > 0 && (
          <>
            {filteredPassageFindings.length > 0 && filteredBookFindings.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Passage-Specific Findings
              </div>
            )}
            {filteredPassageFindings.map((finding, idx) => {
              const itemId = `passage-finding-${idx}`;
              return (
                <ArchaeologicalFindingCard
                  key={itemId}
                  finding={finding}
                  bookName={bookName}
                  isExpanded={expandedItems.has(itemId)}
                  onToggleExpansion={() => toggleExpanded(itemId)}
                />
              );
            })}
          </>
        )}

        {/* Book-level findings */}
        {filteredBookFindings.length > 0 && (
          <>
            {filteredPassageFindings.length > 0 && filteredBookFindings.length > 0 && (
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
                Book-Level Findings
              </div>
            )}
            {filteredBookFindings.map((finding, idx) => {
              const itemId = `book-finding-${idx}`;
              return (
                <ArchaeologicalFindingCard
                  key={itemId}
                  finding={finding}
                  bookName={bookName}
                  isExpanded={expandedItems.has(itemId)}
                  onToggleExpansion={() => toggleExpanded(itemId)}
                />
              );
            })}
          </>
        )}
      </div>
    );
  }

  // Manuscript Study Guidance
  if (capability === 'manuscript-study') {
    return (
      <div className="space-y-6 py-4">
        <div className="bg-primary/5 border border-primary/10 rounded-2xl p-6 text-center space-y-4">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Pencil className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-lg">Interactive Marking Task</h4>
            <p className="text-sm text-balance text-muted-foreground leading-relaxed">
              This part of the discovery happens directly on the biblical text. Use the marking tools at the bottom of your screen to engage with this passage.
            </p>
          </div>
        </div>

        {itemTitles.size > 0 && (
          <div className="space-y-3">
            <h5 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 px-1">Items to Identify</h5>
            <div className="grid gap-3">
              {Array.from(itemTitles).map((title, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-muted/30 p-4 rounded-xl border border-muted-foreground/5">
                  <div className="w-2 h-2 rounded-full bg-primary/40" />
                  <span className="font-medium capitalize">{title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-center pt-4">
          <Button onClick={onClose} className="rounded-full px-8 shadow-md">
            I'm Ready to Mark
          </Button>
        </div>
      </div>
    );
  }

  // Fallback - show generic message
  return (
    <div className="text-center py-8 text-muted-foreground">
      <p>Content for {capability}{feature ? `:${feature}` : ''} coming soon.</p>
    </div>
  );
}

function NoMatchingItems() {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <p>No matching items found in this passage's data.</p>
    </div>
  );
}

export function CapabilityContextModal({
  passageId,
  capabilityRef,
  capabilityItems,
  isOpen,
  onClose,
  bookName
}: CapabilityContextModalProps) {
  let { capability, feature } = parseCapabilityRef(capabilityRef);

  // If feature is missing, try to infer it from the capability items
  if (!feature && capability === 'cultural-context') {
    const inferredFeature = inferFeatureFromItems(capability, capabilityItems);
    if (inferredFeature) {
      console.warn(`Missing feature in capabilityRef "${capabilityRef}". Inferred feature: "${inferredFeature}" from items:`, capabilityItems);
      feature = inferredFeature;
    }
  }

  // Fetch data based on capability type
  const { data: cultureData, isLoading: cultureLoading } = useCultureByPassageData(
    capability === 'cultural-context' ? passageId : undefined
  );
  const cultureByBookContext = use(CultureByBookDataContext);
  const cultureByBookData = bookName ? cultureByBookContext?.getCultureByBookByBook(bookName) : undefined;

  const { data: peopleData, isLoading: peopleLoading } = usePeopleByPassageData(
    capability === 'cultural-context' && feature === 'people' ? passageId : undefined
  );
  const peopleContext = use(PeopleDataContext);

  const { data: historicalData, isLoading: historicalLoading } = useHistoricalEventsByPassageData(
    capability === 'historical-context' ? passageId : undefined
  );
  const historicalByBookContext = use(HistoricalEventsByBookDataContext);
  const historicalByBookData = bookName ? historicalByBookContext?.getHistoricalEventsByBookByBook(bookName) : undefined;

  const { data: literaryData, isLoading: literaryLoading } = useLiteraryFeaturesByPassageData(
    (capability === 'literary-context' || capability === 'scriptural-context') ? passageId : undefined
  );
  const literaryByBookContext = use(LiteraryFeaturesDataContext);
  const literaryByBookData = bookName ? literaryByBookContext?.getLiteraryFeaturesByBook(bookName) : undefined;

  const outlineContext = use(OutlineDataContext);
  const outline = bookName ? outlineContext?.data?.items.find(o => o.book === bookName) : undefined;

  const passageContext = use(PassageDataContext);
  const passage = passageContext?.data?.items.find(p => p.id === passageId);

  const { data: archaeologyData, isLoading: archaeologyLoading } = useArchaeologyByPassageData(
    capability === 'archaeological-context' ? passageId : undefined
  );
  const archaeologyByBookContext = use(ArchaeologyByBookDataContext);
  const archaeologyByBookData = bookName ? archaeologyByBookContext?.getArchaeologyByBookByBook(bookName) : undefined;

  const isLoading = cultureLoading || peopleLoading || historicalLoading || literaryLoading || archaeologyLoading;

  // Track expanded items
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Get the item titles we're looking for (including cleaned versions for better matching)
  const itemTitles = useMemo(() => {
    const titles = new Set<string>();
    capabilityItems.forEach(item => {
      const lower = item.itemTitle.toLowerCase();
      titles.add(lower);

      // Also add a "cleaned" version that removes text in parentheses
      // e.g., "Shepherd (ro'eh)" -> "shepherd"
      const cleaned = lower.replace(/\s*\([^)]*\)/g, '').trim();
      if (cleaned && cleaned !== lower) {
        titles.add(cleaned);
      }

      // Also add the content INSIDE the parentheses as a potential match
      // e.g., "Shepherd (ro'eh)" -> "ro'eh"
      const parenMatch = lower.match(/\(([^)]+)\)/);
      if (parenMatch && parenMatch[1]) {
        titles.add(parenMatch[1].trim());
      }
    });
    return titles;
  }, [capabilityItems]);


  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden pb-6">
        <DialogTitle className="sr-only">
          {getCapabilityTitle(capability, feature)}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Detailed information about the {getCapabilityTitle(capability, feature).toLowerCase()} of this passage.
        </DialogDescription>
        <DialogHeader className="p-6 pb-2">
          <div className="text-xl font-semibold pr-8 flex items-center gap-2">
            {getCapabilityIcon(capability, feature)}
            {getCapabilityTitle(capability, feature)}
          </div>
        </DialogHeader>

        {/* Items being referenced - with padding */}
        {/* <div className="flex flex-wrap gap-2 mb-0 -mt-4 px-6">
          {capabilityItems.map((item, idx) => (
            <Badge key={idx} variant="secondary">
              {item.itemTitle}
            </Badge>
          ))}
        </div> */}

        {/* Content area - scrollable with padding */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 -mt-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <CapabilityContent
              capability={capability}
              feature={feature}
              itemTitles={itemTitles}
              cultureData={cultureData}
              cultureByBookData={cultureByBookData}
              peopleData={peopleData}
              peopleContext={peopleContext}
              historicalData={historicalData}
              historicalByBookData={historicalByBookData}
              literaryData={literaryData}
              literaryByBookData={literaryByBookData}
              outline={outline}
              passage={passage}
              archaeologyData={archaeologyData}
              archaeologyByBookData={archaeologyByBookData}
              bookName={bookName}
              expandedItems={expandedItems}
              toggleExpanded={toggleExpanded}
              onClose={onClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

